import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { fetchAuthSession, JWT, signIn, SignInOutput, signInWithRedirect, signOut } from 'aws-amplify/auth';
import { Observable, Observer } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UrlBuilder } from '../../shared/utils/url-utils';
import { RequestServiceBase } from '../services/request.service.base';
import { AppUrls } from '../../routing/app-urls';

const AUTHENTICATION_URLS = {
    USER_IDP_INFO: 'auth/user-idp-info',
};

@Injectable({ providedIn: 'root' })
export class AuthenticationService extends RequestServiceBase {
    private http = inject(HttpClient);

    public async isSessionValid(): Promise<boolean> {
        try {
            const accessToken = await this.getToken();
            if (accessToken) {
                return true;
            } else {
                return false;
            }
        } catch (err) {
            return false;
        }
    }

    public getUserIdpInfo(usernameOrEmail: string): Observable<UserIdpInfo> {
        const urlBuilder = new UrlBuilder(AUTHENTICATION_URLS.USER_IDP_INFO).addQueryParameter('usernameOrEmail', usernameOrEmail);
        const url = this.resolveUrl(urlBuilder.toUrl());
        const headers = new HttpHeaders({
            'Content-Type': 'application/json'
        });
        return this.http.get<UserIdpInfo>(url,
            {
                headers: headers,
                withCredentials: true
            });
    }

    public async signIn(username: string, password: string): Promise<boolean> {
        await signOut();
        const signInResult: SignInOutput = await signIn({
            username,
            password,
            options: {
                authFlowType: 'USER_PASSWORD_AUTH',
            }
        });

        return signInResult.isSignedIn;
    }

    public async federatedSignIn(idpName: string): Promise<boolean> {
        await signInWithRedirect({
            provider: {
                custom: idpName,
            }
        });

        return true;
    }

    public getTokensForExport(): Record<string, string> {
        const prefix = `CognitoIdentityServiceProvider.${environment.cognito.userPoolClientId}`;
        const tokens: Record<string, string> = {};

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(prefix)) {
                const value = localStorage.getItem(key);
                if (value !== null) tokens[key] = value;
            }
        }

        return tokens;
    }

    public async signOut() {
        await signOut({ global: true });
        this.storeManager.resetState();
        this.router.navigate([AppUrls.signInUrl]);
    }

    public tryRefreshSecurityToken(): Observable<string | null> {
        return new Observable((observer: Observer<string | null>) => {
            this.getToken().then((token) => {
                if (token) {
                    observer.next(token.toString());
                    observer.complete();
                } else {
                    observer.next(null);
                    observer.complete();
                }
            });
        });
    }

    public async getToken(): Promise<JWT | null> {
        try {
            const session = await this.getSession();
            if (session && session.tokens && session.tokens.accessToken) {
                return session.tokens.accessToken;
            } else {
                return null;
            }
        } catch (err) {
            // in case both accessToken and refreshToken are expired, an error will be thrown
            return null;
        }
    }

    private async getSession(forceRefresh: boolean = false) {
        // if the accessToken is expired, but the refreshToken is still valid, fetchAuthSession will always refresh the accessToken (regardless of forceRefresh)
        return await fetchAuthSession({
            forceRefresh,
        });
    }
}

export interface UserIdpInfo {
    valid: boolean;
    msg: string;
    username: string;
    email: string;
    idp: string;
}
